import React from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';

import { SBER_QR_PALETTE } from '../../constants/sber-qr-palette.constant';
import type { MobileStep } from '../SberQrPaymentDetails/SberQrPaymentDetails';

const STEP_INDICES = [0, 1, 2] as const;
const MOBILE_STEP_ORDER: MobileStep[] = ['save', 'pay', 'gallery'];

type DesktopStepsBlockProps = {
  t: (key: string) => string;
};

const DesktopStepsBlock: React.FC<DesktopStepsBlockProps> = ({ t }) => (
  <ol className="tw-grid tw-gap-3 tw-mt-7 tw-p-0 tw-list-none">
    {STEP_INDICES.map((i) => (
      <li
        key={i}
        style={{ borderColor: SBER_QR_PALETTE.line }}
        className="tw-grid tw-grid-cols-[40px_1fr] tw-gap-[10px] tw-items-center tw-px-[15px] tw-py-[13px] tw-border tw-border-solid tw-rounded-[16px] tw-bg-[rgba(16,35,52,.65)]"
      >
        <b
          style={{
            color: SBER_QR_PALETTE.bright,
            background: SBER_QR_PALETTE.brightBg,
            borderColor: SBER_QR_PALETTE.line,
          }}
          className="tw-grid tw-place-items-center tw-w-[38px] tw-h-[38px] tw-rounded-[9px] tw-text-[12px] tw-border tw-border-solid"
        >
          {i + 1}
        </b>
        <span>
          <strong
            className="tw-block tw-text-[14px]"
            style={{ color: SBER_QR_PALETTE.text }}
          >
            {t(`desktop_steps.${i}.label`)}
          </strong>
          <small
            style={{ color: SBER_QR_PALETTE.muted }}
            className="tw-block tw-mt-[2px] tw-text-[11px]"
          >
            {t(`desktop_steps.${i}.hint`)}
          </small>
        </span>
      </li>
    ))}
  </ol>
);

type MobileStepsBlockProps = {
  currentStep: MobileStep;
  t: (key: string) => string;
};

const MobileStepsBlock: React.FC<MobileStepsBlockProps> = ({
  currentStep,
  t,
}) => {
  const currentIndex = MOBILE_STEP_ORDER.indexOf(currentStep);

  return (
    <ol
      className="tw-grid tw-gap-[7px] tw-p-[13px] tw-list-none tw-border tw-border-solid tw-rounded-[32px] tw-bg-[rgba(16,35,52,.75)]"
      style={{ borderColor: SBER_QR_PALETTE.line }}
    >
      {STEP_INDICES.map((i) => {
        const isDone = i < currentIndex;
        const isActive = i === currentIndex;

        return (
          <li
            key={i}
            className="tw-grid tw-grid-cols-[30px_1fr] tw-gap-[10px] tw-items-center tw-p-[5px] tw-transition-opacity tw-duration-300"
            style={{ opacity: isActive ? 1 : isDone ? 0.8 : 0.45 }}
          >
            <b
              style={
                isDone
                  ? {
                      background: SBER_QR_PALETTE.green,
                      color: '#04170e',
                      borderColor: 'transparent',
                    }
                  : isActive
                  ? {
                      background: SBER_QR_PALETTE.brightBg,
                      color: SBER_QR_PALETTE.bright,
                      borderColor: 'rgba(55,235,160,.3)',
                    }
                  : {
                      color: SBER_QR_PALETTE.muted,
                      borderColor: SBER_QR_PALETTE.line,
                    }
              }
              className="tw-grid tw-place-items-center tw-w-[28px] tw-h-[28px] tw-rounded-[9px] tw-text-[12px] tw-border tw-border-solid"
            >
              {i + 1}
            </b>
            <span>
              <strong
                className="tw-block tw-text-[14px]"
                style={{ color: SBER_QR_PALETTE.text }}
              >
                {t(`mobile_steps.${i}.label`)}
              </strong>
              <small
                style={{ color: SBER_QR_PALETTE.muted }}
                className="tw-block tw-mt-[2px] tw-text-[11px]"
              >
                {t(`mobile_steps.${i}.hint`)}
              </small>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

type SberQrInstructionWrapperProps = {
  mobileStep: MobileStep;
  className?: string;
};

export const SberQrInstructionWrapper: React.FC<
  SberQrInstructionWrapperProps
> = ({ mobileStep, className }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sber_qr',
  });

  return (
    <div className={className}>
      <small
        style={{ color: SBER_QR_PALETTE.bright }}
        className="tw-hidden md:tw-inline tw-text-[12px] tw-font-extrabold tw-tracking-[0.12em] tw-uppercase"
      >
        {t('desktop_kicker')}
      </small>

      <h1
        style={{ color: SBER_QR_PALETTE.text }}
        className="tw-hidden md:tw-block tw-mt-3 tw-mb-5 tw-text-[clamp(38px,4.2vw,62px)] tw-leading-[1.04] tw-tracking-[-0.045em]"
      >
        {t('desktop_title')}
      </h1>

      <p
        style={{ color: SBER_QR_PALETTE.muted }}
        className="tw-hidden md:tw-block tw-text-[18px] tw-leading-[1.55]"
      >
        {t('desktop_description')}
      </p>

      <div className="md:tw-hidden">
        <MobileStepsBlock currentStep={mobileStep} t={t} />
      </div>
      <div className="tw-hidden md:tw-block">
        <DesktopStepsBlock t={t} />
      </div>
    </div>
  );
};
